import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { usePackagingPOActions } from 'components/packaging/components/packagingPOV2/hooks/usePackagingPOActions';
import { PackagingPOTransformConfig } from 'components/packaging/components/packagingPOV2/PackagingPOFilterConfig';
import { PackagingPOFilters } from 'components/packaging/components/packagingPOV2/PackagingPOFilters';
import { PackagingPOTable } from 'components/packaging/components/packagingPOV2/PackagingPOTable';
import { usePackagingPurchaseOrdersV2Search } from 'components/packaging/components/packagingPOV2/queries/PackagingPOQueries';
import { ReasonCodeDialog } from 'components/procurement/components/purchaseOrdersV2/ReasonCodeDialog';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi } from 'shared';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function ViewPackagingPOV2() {
    const history = useHistory();
    const location = useLocation();
    useContext(UserContext); // Provider supplies context; facilityID could be used for default filters later

    const [initialState] = useState(() => {
        const parsed = typeof window !== 'undefined' ? fromUrl(location.search, PackagingPOTransformConfig) : {};
        return {
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
        };
    });

    const [filters, setFilters] = useState(initialState);
    const [currentCursor, setCurrentCursor] = useState(undefined);
    const [cursorHistory, setCursorHistory] = useState([]);
    const storedTotalCountRef = useRef(undefined);

    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;

    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, PackagingPOTransformConfig);
        if (!transformed.pageSize) {
            transformed.pageSize = DEFAULT_PAGE_SIZE;
        }
        return transformed;
    }, [filters]);

    const paginatedFilters = useMemo(
        () => ({
            ...apiFilters,
            cursor: currentCursor,
            ...(currentCursor !== undefined &&
                storedTotalCountRef.current !== undefined && {
                    totalCount: storedTotalCountRef.current,
                }),
        }),
        [apiFilters, currentCursor],
    );

    const paginatedQuery = usePackagingPurchaseOrdersV2Search(paginatedFilters, {
        enabled: true,
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const parsed = fromUrl(location.search, PackagingPOTransformConfig);
        const nextState = {
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
        };
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
        setFilters(nextState);
    }, [location.search]);

    useEffect(() => {
        if (paginatedQuery.data?.pagination?.totalCount !== undefined) {
            storedTotalCountRef.current = paginatedQuery.data.pagination.totalCount;
        }
    }, [paginatedQuery.data?.pagination?.totalCount]);

    const { data, error, isFetching, isLoading } = paginatedQuery;
    const { refetch } = paginatedQuery;
    const rows = useMemo(() => data?.rows ?? [], [data]);
    const pagination = useMemo(() => data?.pagination ?? {}, [data]);
    const totalCount = pagination.totalCount ?? storedTotalCountRef.current ?? 0;
    const errorMessage = error?.message ?? '';

    const getFiltersForBackNav = useCallback(
        () =>
            (typeof location?.search === 'string' && location.search ? location.search : '') ||
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
            '',
        [location?.search],
    );

    const { cancelDialog, cancelState, confirmCancel, downloadPdf, downloadShipList, duplicate, sendToVendor } =
        usePackagingPOActions({
            getFiltersForBackNav,
            history,
            refetch,
        });

    const handleFilterChange = useCallback(
        (nextFilters) => {
            const updatedState = {
                ...nextFilters,
                pageSize: filters.pageSize || DEFAULT_PAGE_SIZE,
            };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(updatedState);
            const cleanedFilters = removeEmptyFilters(updatedState);
            pushToUrl(cleanedFilters, PackagingPOTransformConfig, { replace: false });
        },
        [filters.pageSize],
    );

    const handleNextPage = useCallback(() => {
        if (pagination.hasMore && pagination.nextCursor) {
            setCursorHistory((prev) => [...prev, currentCursor]);
            setCurrentCursor(pagination.nextCursor);
        }
    }, [pagination, currentCursor]);

    const handlePreviousPage = useCallback(() => {
        if (cursorHistory.length > 0) {
            const newHistory = [...cursorHistory];
            const previousCursor = newHistory.pop();
            setCursorHistory(newHistory);
            setCurrentCursor(previousCursor);
        }
    }, [cursorHistory]);

    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            const nextState = { ...filters, pageSize: newPageSize };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), PackagingPOTransformConfig, { replace: true });
        },
        [filters],
    );

    const handleCreatePurchaseOrder = useCallback(() => {
        const purchaseOrdersFilters =
            (typeof location?.search === 'string' && location.search ? location.search : '') ||
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
            '';

        history.push({
            pathname: '/packaging/createPurchaseOrder-v2',
            state: {
                packagingPurchaseOrdersFilters: purchaseOrdersFilters,
                purchaseOrdersFilters,
            },
        });
    }, [history, location?.search]);

    const handleBulkCreatePurchaseOrders = useCallback(() => {
        const purchaseOrdersFilters =
            (typeof location?.search === 'string' && location.search ? location.search : '') ||
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
            '';

        history.push({
            pathname: '/packaging/bulkCreatePurchaseOrders-v2',
            state: {
                packagingPurchaseOrdersFilters: purchaseOrdersFilters,
                purchaseOrdersFilters,
            },
        });
    }, [history, location?.search]);

    const currentPage = cursorHistory.length + 1;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    const filterValuesForUI = useMemo(() => {
        const { pageSize: _ps, ...filterOnly } = filters;
        return filterOnly;
    }, [filters]);

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-semibold">Packaging Purchase Orders V2</h1>
                    <div>
                        <Button size="sm" className="mr-2" onClick={handleBulkCreatePurchaseOrders}>
                            Bulk Create Purchase Orders
                        </Button>
                        <Button size="sm" onClick={handleCreatePurchaseOrder}>
                            Create Purchase Order
                        </Button>
                    </div>
                </div>
                <div className="mt-4">
                    <PackagingPOFilters initialValues={filterValuesForUI} onChange={handleFilterChange} />
                </div>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-6 pt-4">
                {errorMessage ? (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {errorMessage}
                    </div>
                ) : null}
                <PackagingPOTable
                    data={rows}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    totalCount={totalCount}
                    hasNextPage={pagination.hasMore}
                    hasPrevious={cursorHistory.length > 0}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onDuplicatePurchaseOrder={duplicate}
                    onDownloadPdf={downloadPdf}
                    onDownloadShipList={downloadShipList}
                    onSendToVendor={sendToVendor}
                    onCancelPurchaseOrder={cancelDialog.open}
                />
            </div>
            <ReasonCodeDialog
                open={cancelDialog.isOpen}
                title="Select A Reason Code and Confirm"
                onCancel={cancelDialog.close}
                onConfirm={confirmCancel}
                isSubmitting={cancelState.isSubmitting}
            />
        </div>
    );
}

ViewPackagingPOV2.displayName = 'ViewPackagingPOV2';
